import React, { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ArrowRight, Lock, Download, FileText, Maximize2, Stamp } from "lucide-react";
import { brand } from "../mock/content";
import { Link } from "../lib/LocalizedLink";
import { ImageLightbox } from "./ImageLightbox";
import { t } from "../lib/ui";

const API = process.env.REACT_APP_BACKEND_URL;
const CLIENT_TOKEN_KEY = "torri_client_token";

export default function TechModal({ data, onClose }) {
  const [slide, setSlide] = useState(0);
  const [isClient, setIsClient] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [patentZoom, setPatentZoom] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(CLIENT_TOKEN_KEY) || "";
    if (!token) { setIsClient(false); return; }
    fetch(`${API}/api/client/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setIsClient(!!d))
      .catch(() => setIsClient(false));
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.classList.add("tech-modal-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      document.body.classList.remove("tech-modal-open");
    };
  }, [onClose]);

  if (!data) return null;
  const n = data.slides.length;
  const go = (dir) => setSlide((s) => (s + dir + n) % n);

  return (
    <div className="fixed inset-0 z-[80] flex items-start md:items-center justify-center p-0 md:p-6" data-testid="tech-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="tech-modal-overlay" />
      <div className="relative w-full md:max-w-4xl max-h-full md:max-h-[92vh] overflow-y-auto bg-[#0a0a0a] text-white border border-white/10 md:rounded-lg shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 md:px-10 py-5 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/10">
          <div>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/60" style={{ fontFamily: "Space Grotesk" }}>{data.caption}</span>
            <h2 className="mt-1 font-black text-2xl md:text-4xl tracking-tight" style={{ fontFamily: "Archivo" }}>{data.code}</h2>
          </div>
          <button onClick={onClose} data-testid="tech-modal-close" aria-label="Chiudi" className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="px-6 md:px-10 py-8 space-y-12">
          {/* Testo + media */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <p className="text-white/70 text-base md:text-lg leading-relaxed order-2 md:order-1" style={{ fontFamily: "Inter" }}>{data.intro}</p>
            {data.mediaFit === "contain" ? (
              <div className="order-1 md:order-2 flex justify-center" data-testid="tech-modal-media-wrap">
                <img src={data.media} alt={data.code} className="h-72 md:h-96 w-auto max-w-full object-contain rounded-lg border border-white/10 bg-white" data-testid="tech-modal-media" />
              </div>
            ) : (
              <div className="order-1 md:order-2 border border-white/10 rounded-lg overflow-hidden bg-white">
                <img src={data.media} alt={data.code} className="w-full h-56 md:h-64 object-cover" data-testid="tech-modal-media" />
              </div>
            )}
          </div>

          {/* Documento scaricabile (gated Area Clienti) */}
          {data.document && (
            <div className="bg-white text-black border border-white/10 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-testid="tech-modal-document">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="flex items-center justify-center h-11 w-11 rounded-full shrink-0" style={{ background: brand.yellow }}><FileText size={20} className="text-black" /></span>
                <div className="min-w-0">
                  <p className="font-bold truncate" style={{ fontFamily: "Archivo" }}>{data.document.name}</p>
                  <p className="text-black/50 text-sm truncate" style={{ fontFamily: "Inter" }}>{data.document.meta}</p>
                </div>
              </div>
              {isClient ? (
                <a
                  href={data.document.url}
                  target="_blank"
                  rel="noreferrer"
                  download
                  data-testid="tech-modal-document-download"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto shrink-0 px-4 py-2.5 bg-black text-white text-xs uppercase tracking-[0.12em] font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
                  style={{ fontFamily: "Space Grotesk" }}
                >
                  {data.document.download} <Download size={15} />
                </a>
              ) : (
                <Link
                  to="/area-clienti"
                  onClick={onClose}
                  data-testid="tech-modal-document-locked"
                  className="inline-flex items-center justify-center gap-2 w-full sm:w-auto shrink-0 px-4 py-2.5 border border-black/15 text-black/70 text-xs uppercase tracking-[0.12em] font-semibold hover:bg-black hover:text-white transition-colors whitespace-nowrap"
                  style={{ fontFamily: "Space Grotesk" }}
                >
                  <Lock size={14} /> {data.document.locked}
                </Link>
              )}
            </div>
          )}

          {data.patent && (
            <div className="border rounded-lg p-4 md:p-5 flex items-center gap-4 md:gap-6" style={{ borderColor: brand.yellow, background: "rgba(249,226,18,0.05)" }} data-testid="tech-modal-patent">
              <button type="button" onClick={() => setPatentZoom(true)} aria-label={t("tech_patent_preview")} className="relative shrink-0 w-20 md:w-24 rounded overflow-hidden border border-white/15 bg-white cursor-zoom-in group" data-testid="tech-modal-patent-preview">
                <img src={data.patent.image} alt={t("tech_patent_preview")} className="w-full h-auto block transition-transform duration-300 group-hover:scale-105" />
                <span className="absolute bottom-1 right-1 p-1 bg-black/60 text-white rounded-full"><Maximize2 size={11} /></span>
              </button>
              <div className="min-w-0">
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-bold px-2 py-1 leading-none text-black" style={{ fontFamily: "Space Grotesk", background: brand.yellow }}><Stamp size={11} strokeWidth={2.5} /> {t("tech_patent_badge")}</span>
                <p className="mt-2.5 text-white/75 text-sm leading-relaxed" style={{ fontFamily: "Inter" }}>{t("tech_patent_text")}</p>
                <p className="mt-2 text-white text-sm font-semibold" style={{ fontFamily: "Space Grotesk" }} data-testid="tech-modal-patent-number">
                  {t("tech_patent_num")} {data.patent.number} <span className="text-white/60 font-normal">·</span> {t("tech_patent_date")} {data.patent.date}
                </p>
              </div>
              {patentZoom && <ImageLightbox slides={[{ image: data.patent.image, caption: `${t("tech_patent_num")} ${data.patent.number} · ${data.patent.date}` }]} index={0} onIndex={() => {}} onClose={() => setPatentZoom(false)} />}
            </div>
          )}

          {/* 3 hotpoint */}
          <div className={`grid grid-cols-1 ${data.points.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-px bg-white/10 border border-white/10 rounded-lg overflow-hidden`} data-testid="tech-modal-points">
            {data.points.map((p) => (
              <div key={p.l} className="bg-[#0a0a0a] p-6 flex flex-col">
                <div className="font-black text-3xl md:text-4xl tracking-tight" style={{ fontFamily: "Archivo", color: brand.yellow }}>{p.k}</div>
                <div className="mt-2 text-sm font-bold uppercase tracking-[0.08em]" style={{ fontFamily: "Space Grotesk" }}>{p.l}</div>
                <p className="mt-2 text-white/55 text-sm leading-relaxed" style={{ fontFamily: "Inter" }}>{p.v}</p>
              </div>
            ))}
          </div>

          {/* Slider foto — oppure video se presente mediaVideo */}
          {data.mediaVideo ? (
            <div className="border border-white/10 rounded-lg overflow-hidden bg-black" data-testid="tech-modal-video-block">
              <video src={data.mediaVideo} className="w-full aspect-video object-contain bg-black block" autoPlay muted loop playsInline controls data-testid="tech-modal-video" />
            </div>
          ) : (
          <div data-testid="tech-modal-slider">
            <div className="border border-white/10 rounded-lg overflow-hidden bg-[#111]">
              <div className={`relative ${data.slidesFit === "contain" ? "bg-white" : "bg-black"}`}>
                <img src={data.slides[slide].image} alt={`${data.code} ${slide + 1}`} onClick={() => setLightbox(true)} className={`block w-full h-64 md:h-96 cursor-zoom-in ${data.slidesFit === "contain" ? "object-cover md:object-contain" : "object-cover"}`} data-testid="tech-modal-slide-img" />
                <span className="pointer-events-none absolute top-3 right-3 p-1.5 bg-black/55 text-white rounded-full" data-testid="tech-modal-zoom-hint"><Maximize2 size={14} /></span>
                <button onClick={() => go(-1)} data-testid="tech-modal-prev" aria-label="Precedente" className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/55 hover:bg-black text-white rounded-full transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <button onClick={() => go(1)} data-testid="tech-modal-next" aria-label="Successivo" className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/55 hover:bg-black text-white rounded-full transition-colors">
                  <ChevronRight size={20} />
                </button>
              </div>
              <div className="flex items-start gap-4 px-5 py-4 border-t-2" style={{ borderColor: brand.yellow }} data-testid="tech-modal-slide-caption">
                <span className="shrink-0 text-xs font-bold tabular-nums pt-0.5" style={{ fontFamily: "Space Grotesk", color: brand.yellow }}>{slide + 1}/{n}</span>
                <p className="text-white/85 text-sm md:text-base leading-snug" style={{ fontFamily: "Inter" }}>{data.slides[slide].caption || data.code}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-2">
              {data.slides.map((_, i) => (
                <button key={i} onClick={() => setSlide(i)} aria-label={`Vai alla slide ${i + 1}`} data-testid={`tech-modal-dot-${i}`}
                  className="tap-xl h-1.5 rounded-full transition-all"
                  style={{ width: i === slide ? 28 : 10, background: i === slide ? brand.yellow : "rgba(255,255,255,0.25)" }} />
              ))}
            </div>
            {lightbox && <ImageLightbox slides={data.slides} index={slide} onIndex={setSlide} onClose={() => setLightbox(false)} />}
          </div>
          )}

          {/* Relazione di ricerca (opzionale) */}
          {data.research && (
            <div className="border-t border-white/10 pt-10" data-testid="tech-modal-research">
              <h3 className="font-black text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "Archivo" }}>{data.research.title}</h3>
              <div className="mt-5 space-y-4">
                {data.research.intro.map((p, i) => (
                  <p key={i} className="text-white/70 text-base leading-relaxed" style={{ fontFamily: "Inter" }}>{p}</p>
                ))}
              </div>

              <div className="mt-8 space-y-4">
                {data.research.stats.map((s, i) => (
                  <div key={i} className="border border-white/10 rounded-lg bg-white/[0.02] p-6 md:p-8" style={{ boxShadow: `inset 3px 0 0 0 ${brand.yellow}` }} data-testid={`tech-modal-research-stat-${i}`}>
                    <div className="font-black text-4xl md:text-5xl tracking-tight" style={{ fontFamily: "Archivo", color: brand.yellow }}>{s.k}</div>
                    <div className="mt-2 text-sm md:text-base font-bold uppercase tracking-[0.06em] text-white/90" style={{ fontFamily: "Space Grotesk" }}>{s.l}</div>
                    <div className="mt-4 space-y-3">
                      {s.v.map((p, j) => (
                        <p key={j} className="text-white/60 text-sm md:text-base leading-relaxed" style={{ fontFamily: "Inter" }}>{p}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 space-y-4">
                {data.research.closing.map((p, i) => (
                  <p key={i} className="text-white/70 text-base leading-relaxed" style={{ fontFamily: "Inter" }}>{p}</p>
                ))}
              </div>

              {data.research.cta && (
                <Link
                  to="/area-clienti"
                  onClick={onClose}
                  data-testid="tech-modal-research-cta"
                  className="mt-8 inline-flex items-center gap-3 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] transition-opacity hover:opacity-90"
                  style={{ fontFamily: "Space Grotesk", background: brand.yellow, color: "#000" }}
                >
                  <Lock size={16} /> {data.research.cta} <ArrowRight size={16} />
                </Link>
              )}

              {data.research.disclaimer && (
                <p className="mt-5 text-white/60 text-xs leading-relaxed" style={{ fontFamily: "Inter" }}>{data.research.disclaimer}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
